/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/number/ToFileSize.ts
 * @create:      2025-06-27 10:43:28.154
 * @modify:      2026-03-12 14:03:36.175
 * @version:     6.1.1
 * @times:       6
 * @lines:       68
 * @copyright:   Copyright © 2018-2026 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Converts a number of bytes into a human-readable file size string.
 *
 * @param bytes - The number of bytes to convert. Can be a `number` or `bigint`.
 * @param baseOn1024 - If `true`, uses base 1024 (e.g., KiB, MiB). If `false`, uses base 1000 (e.g., kB, MB). Defaults to `false`.
 * @returns A string representing the file size in a human-readable format (e.g., "1.23 MB", "456.00 Bytes").
 * 
 * @since 3.0.0
 * @version 2026-03-12
 */
export function ToFileSize(bytes: number | bigint, baseOn1024 = false) {
    if (typeof bytes === "bigint") {
        bytes = Number(bytes);
    }

    const unitConfig = baseOn1024 ? [1024, "K", "iB"] : [1e3, "k", "B"];
    const baseValue = unitConfig[0] as number;
    const unitPrefixBase = unitConfig[1] as string;
    const unitSuffix = unitConfig[2] as string;

    const logFn = Math.log;
    const exponent = (logFn(bytes) / logFn(baseValue)) | 0;

    const scaledValue = bytes / Math.pow(baseValue, exponent);
    const valueText = scaledValue.toFixed(2);

    /* cSpell:disable */
    const unitLabel = exponent
        ? (unitPrefixBase + "MGTPEZY")[exponent - 1] + unitSuffix
        : "Bytes";
    /* cSpell:enable */

    const result = valueText + " " + unitLabel;
    return result;
}
